import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import { searchKeys } from '@/entities/search';
import { createKnowledgeSet, deleteKnowledgeSet, fetchKnowledgeSet, updateKnowledgeSet } from './api';
import type {
  KnowledgeSetCreateRequest,
  KnowledgeSetCreateResponse,
  KnowledgeSetUpdateRequest,
  KnowledgeSetUpdateResponse,
} from './types';

/**
 * Query keys for knowledge sets
 */
export const knowledgeSetKeys = {
  all: [API_TAGS.KNOWLEDGE_SETS] as const,
  details: () => [...knowledgeSetKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...knowledgeSetKeys.details(), id] as const,
};

/**
 * Hook to fetch a single knowledge set by ID
 */
export const useKnowledgeSet = (id?: number | string) => {
  return useQuery({
    queryKey: knowledgeSetKeys.detail(id!),
    queryFn: () => fetchKnowledgeSet(id!),
    enabled: !!id,
  });
};

/**
 * Hook to create a knowledge set
 */
export const useCreateKnowledgeSet = () => {
  const queryClient = useQueryClient();

  return useMutation<KnowledgeSetCreateResponse, Error, KnowledgeSetCreateRequest>({
    mutationFn: (payload) => createKnowledgeSet(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: searchKeys.all });
    },
  });
};

/**
 * Hook to update a knowledge set
 */
export const useUpdateKnowledgeSet = () => {
  const queryClient = useQueryClient();

  return useMutation<KnowledgeSetUpdateResponse, Error, { id: number | string; payload: KnowledgeSetUpdateRequest }>({
    mutationFn: ({ id, payload }) => updateKnowledgeSet(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: knowledgeSetKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: searchKeys.all });
    },
  });
};

/**
 * Hook to delete a knowledge set
 */
export const useDeleteKnowledgeSet = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number | string>({
    mutationFn: (id) => deleteKnowledgeSet(id),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: searchKeys.all });
      queryClient.removeQueries({ queryKey: knowledgeSetKeys.detail(deletedId) });
    },
  });
};
